import {throttle} from '@github/mini-throttle'
import {useAliveChannels} from '@github-ui/use-alive'
import useIsMounted from '@github-ui/use-is-mounted'
import {useMemo, useState, useEffect} from 'react'
import {usePageVisibility} from './use-page-visibility'

import type {AliveChannels as MergeBoxAliveChannels} from '../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../page-data/payloads/status-checks'

// Keys are derived from the real payload types, so adding a channel to either
// payload automatically requires a matching `useAlive` subscription below.
// Values are loosened to nullable because callers build channel objects whose
// individual channels may be absent at runtime.
export type MergeabilityLiveUpdateChannels = {
  [K in keyof MergeBoxAliveChannels]: string | null | undefined
} & {
  [K in keyof StatusChecksAliveChannels]?: string | null | undefined
}

// Stable fallback so consuming hooks aren't handed a fresh object every render.
export const EMPTY_CHANNELS: MergeabilityLiveUpdateChannels = {
  stateChannel: null,
  deployedChannel: null,
  reviewStateChannel: null,
  workflowsChannel: null,
  mergeQueueChannel: null,
  headRefChannel: null,
  baseRefChannel: null,
  gitMergeStateChannel: null,
  pullRequestChannel: null,
  codeScanningChannel: null,
  commitHeadShaChannel: null,
} as const

export function useMergeabilityLiveUpdates({
  channels,
  refetchQuery,
  throttleTime = 2000,
}: {
  channels: MergeabilityLiveUpdateChannels | null
  refetchQuery: () => void
  throttleTime?: number
}): void {
  const isMounted = useIsMounted()
  const [hasPendingUpdate, setHasPendingUpdate] = useState(false)
  const isPageVisible = usePageVisibility()

  // There are several PR websocket events that can trigger a refresh of the
  // MergeBox Pull Request data.
  // This creates a throttle (default 2s) to prevent multiple refetches
  // happening in the same time window
  // This is also in place to preventing inflicting a DDOS attack on ourselves
  // by refetching too often.
  const throttleRefetchingPullRequestData = useMemo(
    () =>
      throttle(() => {
        if (isMounted()) {
          refetchQuery()
        }
      }, throttleTime),
    [refetchQuery, isMounted, throttleTime],
  )

  const handleMergabilityChannelNotification = useMemo(
    () => (data: {wait?: number}) => {
      if (isPageVisible) {
        // Some of our WebSocket event notifications include a wait value to assure that we consider replication lag times.
        // We need to avoid calling our refetching method until that wait time is over by using a setTimeout that is equal to the data.wait value, if it exists.
        window.setTimeout(() => throttleRefetchingPullRequestData(), data.wait || 0)
      } else {
        setHasPendingUpdate(true)
      }
    },
    [isPageVisible, throttleRefetchingPullRequestData],
  )

  useEffect(() => {
    if (isMounted() && isPageVisible && hasPendingUpdate) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasPendingUpdate(false)
      refetchQuery()
    }
  }, [isPageVisible, hasPendingUpdate, setHasPendingUpdate, isMounted, refetchQuery])

  // Subscribe to every mergeability channel with a single hook. Deriving the
  // list from `Object.values(channels)` keeps it in sync with the
  // `MergeabilityLiveUpdateChannels` type automatically — adding a field
  // subscribes it with no other changes. useAliveChannels ignores
  // null/undefined channels.
  useAliveChannels(Object.values(channels ?? EMPTY_CHANNELS), handleMergabilityChannelNotification)
}
